// components/ConsultationTracking.jsx
import React, { useState } from "react";
import { Search, Loader2, CheckCircle2, XCircle, Clock, Phone, Mail } from "lucide-react";
import BASE_URL from "../../../../config/config";

const ConsultationTracking = ({ embedded = false }) => {
  const [trackingId, setTrackingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const statusConfig = {
    pending: {
      icon: Clock,
      color: "amber",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-800",
      iconColor: "text-amber-500",
      title: "در انتظار پرداخت",
      description: "سفارش شما ثبت شده اما پرداخت تکمیل نشده است"
    },
    paid: {
      icon: CheckCircle2,
      color: "green",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      textColor: "text-green-800",
      iconColor: "text-green-500",
      title: "پرداخت موفق",
      description: "پرداخت شما با موفقیت انجام شد و تیم ما به زودی با شما تماس خواهند گرفت"
    },
    confirmed: {
      icon: CheckCircle2,
      color: "blue",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-800",
      iconColor: "text-blue-500",
      title: "تایید شده",
      description: "جلسه شما تایید و زمان آن هماهنگ شده است"
    },
    completed: {
      icon: CheckCircle2,
      color: "purple",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      textColor: "text-purple-800",
      iconColor: "text-purple-500",
      title: "تکمیل شده",
      description: "جلسه مشاوره با موفقیت برگزار شد"
    },
    cancelled: {
      icon: XCircle,
      color: "red",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-800",
      iconColor: "text-red-500",
      title: "لغو شده",
      description: "این سفارش لغو شده است"
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await fetch(
        `${BASE_URL}/consultation/track/${encodeURIComponent(trackingId)}`,
        {
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "خطا در دریافت اطلاعات");
      }

      setResult(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "نامشخص";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch (error) {
      return "نامشخص";
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fa-IR").format(price);
  };

  const StatusIcon = result ? statusConfig[result.order_status]?.icon || Clock : Clock;
  const config = result ? statusConfig[result.order_status] || statusConfig.pending : statusConfig.pending;

  return (
    <div className={`${embedded ? "" : "min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4"}`} dir="rtl">
      <div className={`${embedded ? "w-full" : "max-w-2xl w-full"} bg-white rounded-2xl shadow-xl overflow-hidden`}>
        
        {/* Header */}
        {!embedded && (
          <div className="bg-gradient-to-l from-slate-600 to-slate-700 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">پیگیری سفارش مشاوره</h1>
            <p className="text-slate-300 text-sm">
              با استفاده از شماره سفارش یا کد پیگیری، وضعیت درخواست خود را مشاهده کنید
            </p>
          </div>
        )}

        <div className="p-6">
          
          {/* فرم جستجو */}
          <form onSubmit={handleSearch} className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              شماره سفارش یا کد پیگیری
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  className="w-full pr-10 pl-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-500 focus:border-transparent transition outline-none"
                  placeholder="مثال: 66 یا 16219201"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || !trackingId.trim()}
                className="px-6 py-3 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>جستجو...</span>
                  </>
                ) : (
                  "پیگیری"
                )}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              💡 می‌توانید از شماره سفارش (مثل 66) یا کد پیگیری زرین‌پال (مثل 16219201) استفاده کنید
            </p>
          </form>

          {/* خطا */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-800 font-medium">خطا در جستجو</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* نتیجه */}
          {result && (
            <div className="space-y-4">
              
              {/* وضعیت */}
              <div className={`${config.bgColor} ${config.borderColor} border-2 rounded-xl p-5`}>
                <div className="flex items-start gap-4">
                  <div className={`${config.iconColor} flex-shrink-0`}>
                    <StatusIcon size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold ${config.textColor} mb-1`}>
                      {config.title}
                    </h3>
                    <p className={`text-sm ${config.textColor} opacity-80`}>
                      {config.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* جزئیات سفارش */}
              <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                <h4 className="font-bold text-slate-800 mb-3">جزئیات سفارش</h4>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-600">شماره سفارش:</p>
                    <p className="font-bold text-slate-800">#{result.order_id}</p>
                  </div>
                  
                  {result.zarinpal_ref_id && (
                    <div>
                      <p className="text-slate-600">کد پیگیری:</p>
                      <p className="font-bold text-slate-800">{result.zarinpal_ref_id}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-slate-600">تاریخ ثبت:</p>
                    <p className="font-medium text-slate-800">{formatDate(result.order_created_at)}</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-600">مبلغ:</p>
                    <p className="font-bold text-green-600">{formatPrice(result.amount)} تومان</p>
                  </div>
                </div>
              </div>

              {/* اطلاعات تماس */}
              <div className="bg-slate-50 rounded-xl p-5 space-y-2">
                <h4 className="font-bold text-slate-800 mb-3">اطلاعات تماس</h4>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">نام:</span>
                    <span className="font-medium text-slate-800">{result.full_name}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-slate-500" />
                    <span className="font-medium text-slate-800">{result.phone}</span>
                  </div>
                  
                  {result.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-slate-500" />
                      <span className="font-medium text-slate-800">{result.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* اقدامات بعدی */}
              {result.order_status === "paid" && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <Clock size={18} />
                    مراحل بعدی
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1 mr-6 list-disc">
                    <li>تیم ما ظرف 24 ساعت آینده با شما تماس خواهند گرفت</li>
                    <li>زمان دقیق جلسه با شما هماهنگ می‌شود</li>
                    <li>لینک جلسه آنلاین ارسال خواهد شد</li>
                  </ul>
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ConsultationTracking;
